/**
 * Pop-out windows (split screen, second and portrait monitors): a module
 * opened in its own browser window with a compact header instead of the full
 * shell. The window is an ordinary URL of the same app plus `?pop=<key>`, so
 * it reloads, bookmarks and signs in like any other tab; the root route keeps
 * `pop` (and `pin`) on every navigation inside it (router.tsx).
 *
 *   ?pop=<key>   this window is a pop-out of module <key> (the route's module id)
 *   ?pin=1       locked to the patient it shows; without it a per-patient
 *                pop-out follows the patient selected in the other windows
 *
 * Pure helpers only (tested in popout.test.ts); window.open lives in open.ts.
 */

export const POP_PARAM = 'pop';
export const PIN_PARAM = 'pin';

const KEY_RE = /^[a-z][a-z0-9-]{0,40}$/;
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const PATIENT_PATH = /^\/patients\/([0-9a-f-]{36})(?=\/|$)/i;

export interface PopoutSpec {
  /** The module id (route `staticData.shell.moduleId`). */
  key: string;
  label: string;
  /** The module's own page when no patient is selected. */
  path: string;
  /** The page for one patient (`$patientId`), for modules that follow the patient. */
  patientPath?: string;
  /** Default content size (CSS px) of a first pop-out; later ones reuse the saved size. */
  width: number;
  height: number;
}

/**
 * The large modules offered in the Pop out menu. Any other page can still be
 * popped out from its own screen (the menu's first entry); it then uses
 * `DEFAULT_SIZE`.
 */
export const POPOUT_MODULES: readonly PopoutSpec[] = [
  { key: 'schedule', label: 'Schedule', path: '/schedule', width: 1280, height: 900 },
  {
    key: 'chart',
    label: 'Chart',
    path: '/charting',
    patientPath: '/patients/$patientId/chart',
    width: 1200,
    height: 900,
  },
  {
    key: 'imaging',
    label: 'Imaging',
    path: '/imaging',
    patientPath: '/imaging?patient=$patientId',
    width: 1280,
    height: 900,
  },
  {
    key: 'ledger',
    label: 'Ledger',
    path: '/billing',
    patientPath: '/patients/$patientId/account',
    width: 1100,
    height: 900,
  },
  {
    key: 'perio',
    label: 'Perio',
    path: '/perio',
    patientPath: '/patients/$patientId/perio',
    width: 1200,
    height: 900,
  },
  { key: 'phones', label: 'Phones', path: '/phones', width: 960, height: 900 },
  { key: 'analytics', label: 'Analytics', path: '/analytics', width: 1280, height: 900 },
  { key: 'lightboard', label: 'Light board', path: '/lightboard', width: 960, height: 1000 },
];

export const DEFAULT_SIZE = { width: 1100, height: 850 } as const;
/** Smallest size a saved geometry may restore to (a window dragged to a sliver). */
export const MIN_SIZE = { width: 480, height: 360 } as const;

export function popoutSpec(key: string): PopoutSpec | undefined {
  return POPOUT_MODULES.find((s) => s.key === key);
}

export interface PopoutState {
  key: string;
  /** Locked to the patient on screen (`?pin=1`); else follows the selected patient. */
  pinned: boolean;
}

/** This window's pop-out state from the URL search, or null in a normal window. */
export function readPopout(search: Readonly<Record<string, unknown>>): PopoutState | null {
  const key = search[POP_PARAM];
  if (typeof key !== 'string' || !KEY_RE.test(key)) return null;
  const pin = search[PIN_PARAM];
  return { key, pinned: pin === '1' || pin === 1 || pin === true };
}

/** Same as `readPopout` for a raw query string (`window.location.search`). */
export function readPopoutQuery(query: string): PopoutState | null {
  return readPopout(Object.fromEntries(new URLSearchParams(query)));
}

/**
 * The URL a pop-out opens: `href` (path plus optional query) with `pop`, the
 * office selector and `pin`. Relative (same origin), never absolute.
 */
export function popoutHref(
  href: string,
  opts: { key: string; office?: string | null; pinned?: boolean },
): string {
  const url = new URL(href, 'http://x');
  url.searchParams.set(POP_PARAM, opts.key);
  if (opts.office) url.searchParams.set('office', opts.office);
  if (opts.pinned) url.searchParams.set(PIN_PARAM, '1');
  else url.searchParams.delete(PIN_PARAM);
  return `${url.pathname}${url.search}`;
}

/** The same page as a normal (full shell) URL: `pop` and `pin` removed. */
export function mainHref(href: string): string {
  const url = new URL(href, 'http://x');
  url.searchParams.delete(POP_PARAM);
  url.searchParams.delete(PIN_PARAM);
  return `${url.pathname}${url.search}`;
}

/** The page a module opens for `patientId` (its patient page), else its own page. */
export function specHref(spec: PopoutSpec, patientId: string | null): string {
  if (patientId && UUID.test(patientId) && spec.patientPath) {
    return spec.patientPath.replace('$patientId', encodeURIComponent(patientId));
  }
  return spec.path;
}

/** The patient a pop-out's page shows (`/patients/<id>/...` or `?patient=<id>`). */
export function patientOnPage(
  pathname: string,
  search: Readonly<Record<string, unknown>>,
): string | null {
  const m = PATIENT_PATH.exec(pathname);
  if (m?.[1] && UUID.test(m[1])) return m[1];
  const q = search['patient'];
  return typeof q === 'string' && UUID.test(q) ? q : null;
}

/**
 * The same page for another patient: the id in the path or in `?patient=`
 * replaced. Null when the page is not about one patient.
 */
export function retargetPatient(
  pathname: string,
  search: Readonly<Record<string, unknown>>,
  patientId: string,
): { pathname: string; search: Record<string, unknown> } | null {
  if (!UUID.test(patientId)) return null;
  const m = PATIENT_PATH.exec(pathname);
  if (m?.[1] && UUID.test(m[1])) {
    return { pathname: pathname.replace(m[1], patientId), search: { ...search } };
  }
  const q = search['patient'];
  if (typeof q === 'string' && UUID.test(q)) {
    return { pathname, search: { ...search, patient: patientId } };
  }
  return null;
}

/**
 * Pages of the MAIN window that may switch patient when a pop-out (a
 * schedule on the second monitor) selects someone: the family file, chart,
 * account and imaging. Pages where people type (perio, notes, treatment
 * plans, forms) keep their patient; the selection still changes in the bar.
 */
const MAIN_FOLLOWS = [
  /^\/patients\/[^/]+$/,
  /^\/patients\/[^/]+\/chart$/,
  /^\/patients\/[^/]+\/account$/,
  /^\/imaging$/,
];

export function mainFollows(pathname: string): boolean {
  return MAIN_FOLLOWS.some((re) => re.test(pathname));
}

export interface Geometry {
  left: number;
  top: number;
  width: number;
  height: number;
}

/** A saved geometry, or null when it is not four finite numbers. */
export function parseGeometry(raw: unknown): Geometry | null {
  if (!raw || typeof raw !== 'object') return null;
  const g = raw as Record<string, unknown>;
  const n = (v: unknown) => (typeof v === 'number' && Number.isFinite(v) ? Math.round(v) : null);
  const left = n(g['left']);
  const top = n(g['top']);
  const width = n(g['width']);
  const height = n(g['height']);
  if (left === null || top === null || width === null || height === null) return null;
  if (width <= 0 || height <= 0) return null;
  return { left, top, width, height };
}

/**
 * window.open features for a pop-out. A saved geometry wins (it may sit on
 * another monitor: negative or large left/top are kept, the browser keeps the
 * window on a screen); a first pop-out is the module's default size, placed
 * beside the opener's right edge when it fits on the opener's screen,
 * otherwise centred on it.
 */
export function windowFeatures(
  saved: Geometry | null,
  size: { width: number; height: number },
  screen: {
    availLeft: number;
    availTop: number;
    availWidth: number;
    availHeight: number;
    openerLeft: number;
    openerTop: number;
    openerWidth: number;
  },
): string {
  let g: Geometry;
  if (saved) {
    g = {
      ...saved,
      width: Math.max(MIN_SIZE.width, saved.width),
      height: Math.max(MIN_SIZE.height, saved.height),
    };
  } else {
    const width = Math.min(size.width, screen.availWidth);
    const height = Math.min(size.height, screen.availHeight);
    const right = screen.openerLeft + screen.openerWidth;
    const fitsBeside = right + width <= screen.availLeft + screen.availWidth;
    g = {
      width,
      height,
      left: fitsBeside
        ? right
        : screen.availLeft + Math.max(0, Math.round((screen.availWidth - width) / 2)),
      top: fitsBeside
        ? Math.max(screen.availTop, screen.openerTop)
        : screen.availTop + Math.max(0, Math.round((screen.availHeight - height) / 2)),
    };
  }
  return `popup=yes,width=${g.width},height=${g.height},left=${g.left},top=${g.top}`;
}

/** The window name of a module's pop-out: popping it out again reuses that window. */
export function windowName(key: string): string {
  return `velora-pop-${key}`;
}

/**
 * The pop-out window key for any page (sidebar item, module tab): the big
 * module whose page it is (so it reuses that module's window and size), else
 * a key made from `fallbackId`.
 */
export function popoutKeyFor(href: string, fallbackId: string): string {
  const path = new URL(href, 'http://x').pathname;
  const spec = POPOUT_MODULES.find(
    (s) =>
      s.path === path ||
      (s.patientPath !== undefined &&
        new RegExp(
          `^${(s.patientPath.split('?')[0] ?? '').replace('$patientId', '[0-9a-f-]{36}')}$`,
          'i',
        ).test(path)),
  );
  if (spec) return spec.key;
  const key = fallbackId
    .toLowerCase()
    .replace(/[^a-z0-9-]+/g, '-')
    .replace(/^[^a-z]+/, '')
    .slice(0, 41)
    .replace(/-+$/, '');
  return KEY_RE.test(key) ? key : 'page';
}
